/**
 * useCanvasSelection.ts
 * Hook pour gérer la sélection par rectangle (marquee)
 * React-first: Utilise uniquement le store Zustand comme source de vérité
 */
import { useState, useRef, useCallback } from 'react';
import Konva from 'konva';
import type { ShapeData } from '../../../types/ShapeTypes';
import { getShapesInSelectionRect, type SelectionRect } from './CanvasSelection';

interface UseCanvasSelectionProps {
  shapes: ShapeData[];
  selectedNodeIds: string[]; // Source de vérité: vient uniquement du store Zustand
  selectShapes: (ids: string[]) => void; // Action Zustand pour sélectionner
  clearSelection: () => void; // Action Zustand pour désélectionner
  selectionOffset?: { x: number; y: number };
}

/**
 * Hook pour gérer la sélection par rectangle (marquee)
 *
 * RÈGLE: selectedNodeIds vient UNIQUEMENT du store Zustand
 * Aucun état local ne doit contredire le store
 */
export function useCanvasSelection({
  shapes,
  selectedNodeIds, // Source de vérité unique depuis Zustand
  selectShapes, // Action Zustand
  clearSelection, // Action Zustand
  selectionOffset = { x: 50, y: 50 },
}: UseCanvasSelectionProps) {
  // État local UNIQUEMENT pour l'UI du rectangle de sélection (pas pour la sélection elle-même)
  const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      const target = e.target;
      const stage = target.getStage();
      if (!stage) return;

      const isModifierPressed = e.evt.ctrlKey || e.evt.metaKey;

      interface KonvaNodeWithName extends Konva.Node {
        getName?: () => string;
      }
      const targetName = (target as KonvaNodeWithName).getName?.();
      const parentName = (target.getParent() as KonvaNodeWithName)?.getName?.();

      if (
        target === stage ||
        targetName === 'background-rect' ||
        parentName === 'background-rect'
      ) {
        const pointer = stage.getPointerPosition();
        if (!pointer) return;

        if (!isModifierPressed) {
          clearSelection();
        }

        selectionStartRef.current = { x: pointer.x, y: pointer.y };
        setSelectionRect({ x: pointer.x, y: pointer.y, width: 0, height: 0 });
        setIsSelecting(true);
      }
    },
    [clearSelection]
  );

  const handleMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (!isSelecting || !selectionStartRef.current) return;
      const pointer = e.target.getStage()?.getPointerPosition();
      if (!pointer) return;
      const start = selectionStartRef.current;
      const rect = {
        x: Math.min(start.x, pointer.x),
        y: Math.min(start.y, pointer.y),
        width: Math.abs(pointer.x - start.x),
        height: Math.abs(pointer.y - start.y),
      };
      setSelectionRect(rect);
    },
    [isSelecting]
  );

  const handleMouseUp = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (!isSelecting || !selectionRect) {
        setIsSelecting(false);
        return;
      }
      setIsSelecting(false);
      selectionStartRef.current = null;

      const stage = e.target.getStage();
      if (!stage) return;

      const stagePos = stage.position();
      const stageScale = stage.scaleX();

      const selectedIds = getShapesInSelectionRect(
        selectionRect,
        shapes,
        stagePos,
        stageScale,
        selectionOffset
      );

      const isModifierPressed = e.evt.ctrlKey || e.evt.metaKey;
      // LIRE selectedNodeIds depuis le store (source de vérité unique)
      const currentSelectedIds = selectedNodeIds;

      if (selectedIds.length > 0) {
        if (isModifierPressed) {
          // Mode toggle: ajouter/retirer les shapes sélectionnées
          const newSelectedIds: string[] = [];
          const toToggle = new Set(selectedIds);

          // Garder les shapes déjà sélectionnées qui ne sont pas dans le rectangle
          currentSelectedIds.forEach(id => {
            if (!toToggle.has(id)) {
              newSelectedIds.push(id);
            }
          });

          // Ajouter les shapes dans le rectangle qui ne sont pas déjà sélectionnées
          selectedIds.forEach(id => {
            if (!currentSelectedIds.includes(id)) {
              newSelectedIds.push(id);
            }
          });

          // Mettre à jour le store Zustand (source de vérité unique)
          if (newSelectedIds.length > 0) {
            selectShapes(newSelectedIds);
          } else {
            clearSelection();
          }
        } else {
          // Mode remplacement: remplacer la sélection actuelle
          selectShapes(selectedIds);
        }
      } else if (!isModifierPressed) {
        // Aucune shape dans le rectangle et pas de modificateur: désélectionner
        clearSelection();
      }
      setSelectionRect(null);
    },
    [
      isSelecting,
      selectionRect,
      shapes,
      selectShapes,
      clearSelection,
      selectedNodeIds,
      selectionOffset,
    ]
  );

  return {
    selectionRect,
    isSelecting,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
}
